import { useState } from "react";
import type { ReactElement } from "react";
import type { Unternehmen } from "@kundeninfo/api-client";
import { Icon, useSizeClass, contrastRatio } from "@kundeninfo/ui";
import type { Benutzer } from "../session.js";
import "./Kopfzeile.css";

export type KopfzeileProps = {
  user: Benutzer;
  firma: Unternehmen | null;
  onLogout: () => void;
  onOpenSettings: () => void;
};

function initialen(name: string): string {
  const teile = name.trim().split(/\s+/);
  const erste = teile[0]?.[0] ?? "";
  const letzte = teile.length > 1 ? (teile[teile.length - 1]?.[0] ?? "") : "";
  return (erste + letzte).toUpperCase() || "?";
}

function Markenzeichen({ firma, variante }: { firma: Unternehmen; variante: "wortmarke" | "bildmarke" }): ReactElement {
  const hell = variante === "wortmarke" ? firma.logoWordmark : firma.logoMark;
  const dunkel = variante === "wortmarke" ? firma.logoWordmarkDark : firma.logoMarkDark;

  // Keine Datei überhaupt hinterlegt: Kürzel auf der Markenfarbe als Fläche.
  if (hell === null || hell === undefined) {
    const textHell = contrastRatio(firma.colorHex, "#ffffff") >= 4.5 ? "#ffffff" : "#111111";
    return (
      <span className="kz__abzeichen" style={{ background: firma.colorHex, color: textHell }}>
        {firma.shortCode}
      </span>
    );
  }

  const hatDunkel = dunkel !== null && dunkel !== undefined;
  return (
    <span className={`kz__marke-bild kz__marke-bild--${variante}${hatDunkel ? "" : " kz__marke-bild--nur-hell"}`}>
      <img src={`/logos/${hell}`} alt={firma.name} className="kz__marke-hell" />
      {hatDunkel && <img src={`/logos/${dunkel}`} alt={firma.name} className="kz__marke-dunkel" />}
    </span>
  );
}

/**
 * Kopfzeile: Logo des Heimatunternehmens (Wortmarke ab Tablet, Bildmarke auf dem iPhone),
 * Trenner, Namenszug der Anwendung, ein noch stummer Sucheinstieg (die eigentliche Suche
 * lebt in der Kundenliste, siehe KundenListe.tsx) und das Benutzermenü mit Abmeldung.
 */
export function Kopfzeile({ user, firma, onLogout, onOpenSettings }: KopfzeileProps): ReactElement {
  const klasse = useSizeClass();
  const [menuOffen, setMenuOffen] = useState(false);

  return (
    <div className="kz">
      <div className="kz__logo">
        {firma !== null
          ? <Markenzeichen firma={firma} variante={klasse === "phone" ? "bildmarke" : "wortmarke"} />
          : <span className="kz__platzhalter">Kundeninfo</span>}
      </div>
      {firma !== null && <span className="kz__trenner" aria-hidden="true" />}
      <span className="kz__marketname">Kundeninfo</span>
      <div className="kz__spacer" />
      <button
        type="button"
        className="kz__suche"
        disabled
        aria-label="Suche"
        title="Die übergreifende Suche kommt mit einem späteren Paket."
      >
        <Icon name="magnifying-glass" size={16} />
      </button>
      <div className="kz__benutzer">
        <button
          type="button"
          className="kz__benutzer-knopf"
          aria-haspopup="menu"
          aria-expanded={menuOffen}
          aria-label={`Benutzermenü, angemeldet als ${user.name}`}
          onClick={() => setMenuOffen((offen) => !offen)}
        >
          {initialen(user.name ?? "")}
        </button>
        {menuOffen && (
          <div role="menu" className="kz__menu">
            <p className="kz__menu-name">{user.name}</p>
            <p className="kz__menu-mail">{user.email}</p>
            <button
              type="button"
              role="menuitem"
              className="kz__menu-eintrag"
              onClick={() => { setMenuOffen(false); onOpenSettings(); }}
            >
              <Icon name="gear" size={14} />
              Darstellung
            </button>
            <button type="button" role="menuitem" className="kz__menu-eintrag" onClick={onLogout}>
              <Icon name="lock" size={14} />
              Abmelden
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
